import { useEffect, useState } from 'react';
import { X, Upload } from 'lucide-react';
import type { AssetItem, AssetFolder } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAssets, uploadAsset } from '../lib/api';
import { processImage, type ProcessedImage } from '../lib/imageProcessor';

const FOLDERS: AssetFolder[] = ['members', 'news', 'benefits', 'discography'];

interface ImagePickerModalProps {
  open:          boolean;
  onClose:       () => void;
  onSelect:      (url: string) => void;
  defaultFolder: AssetFolder;
  initialTab?:   'assets' | 'upload';
}

export default function ImagePickerModal({
  open,
  onClose,
  onSelect,
  defaultFolder,
  initialTab = 'assets',
}: ImagePickerModalProps) {
  const { accessToken } = useAuth();
  const [activeTab,     setActiveTab]     = useState<'assets' | 'upload'>(initialTab);
  const [activeFolder,  setActiveFolder]  = useState<AssetFolder>(defaultFolder);
  const [assets,        setAssets]        = useState<AssetItem[]>([]);
  const [loading,       setLoading]       = useState(false);
  const [selectedId,    setSelectedId]    = useState<string | null>(null);
  const [search,        setSearch]        = useState('');
  const [processed,     setProcessed]     = useState<ProcessedImage | null>(null);
  const [processing,    setProcessing]    = useState(false);
  const [uploading,     setUploading]     = useState(false);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab);
    setActiveFolder(defaultFolder);
    setSelectedId(null);
    setSearch('');
    setProcessed(null);
  }, [open, initialTab, defaultFolder]);

  useEffect(() => {
    if (!open || !accessToken) return;
    setLoading(true);
    getAssets(accessToken, activeFolder)
      .then(setAssets)
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, [open, activeFolder, accessToken]);

  async function handleFileSelect(file: File) {
    setProcessing(true);
    try {
      setProcessed(await processImage(file));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Processing failed');
    } finally {
      setProcessing(false);
    }
  }

  async function handleUploadAndPick() {
    if (!processed || !accessToken) return;
    setUploading(true);
    try {
      const asset = await uploadAsset(
        accessToken, processed.blob, `${Date.now()}.webp`,
        activeFolder, processed.width, processed.height
      );
      URL.revokeObjectURL(processed.previewUrl);
      onSelect(asset.url);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function handlePick() {
    const asset = assets.find((a) => a.id === selectedId);
    if (asset) { onSelect(asset.url); onClose(); }
  }

  function clearProcessed() {
    if (processed) URL.revokeObjectURL(processed.previewUrl);
    setProcessed(null);
  }

  const filtered = assets.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative flex h-[80vh] w-full max-w-3xl flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">

        {/* Header tabs */}
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-3">
          <div className="flex gap-1">
            {(['assets', 'upload'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                  activeTab === tab ? 'bg-pink-500/10 text-pink-400' : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                {tab === 'assets' ? 'Assets' : 'Upload'}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-200"><X size={18} /></button>
        </div>

        {/* Folder nav — shared between tabs */}
        <div className="flex gap-2 border-b border-[var(--color-border)] px-5 py-2">
          {FOLDERS.map((folder) => (
            <button
              key={folder}
              type="button"
              onClick={() => setActiveFolder(folder)}
              className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                activeFolder === folder
                  ? 'bg-pink-500/20 text-pink-400'
                  : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
              }`}
            >
              {folder}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'assets' ? (
            <>
              <input
                type="text"
                placeholder="Cari nama file..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-4 w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
              />
              {loading ? (
                <div className="flex justify-center py-10">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-500">Belum ada assets di folder ini.</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {filtered.map((asset) => (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => setSelectedId(asset.id === selectedId ? null : asset.id)}
                      className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-colors ${
                        selectedId === asset.id
                          ? 'border-pink-500'
                          : 'border-[var(--color-border)] hover:border-pink-500/40'
                      }`}
                    >
                      <img src={asset.url} alt={asset.name} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {!processed ? (
                <label className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[var(--color-border)] py-16 transition-colors hover:border-pink-500/30 hover:text-pink-400 ${processing ? 'pointer-events-none opacity-60 text-gray-600' : 'text-gray-500'}`}>
                  <Upload size={32} />
                  <span className="text-sm">{processing ? 'Memproses...' : 'Pilih atau drop gambar di sini'}</span>
                  <span className="text-xs text-gray-600">Dikonversi ke WebP · maks 4900px</span>
                  <input
                    type="file" accept="image/*" className="hidden" disabled={processing}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                  />
                </label>
              ) : (
                <div className="space-y-4">
                  <img src={processed.previewUrl} alt="preview" className="mx-auto max-h-64 rounded-lg object-contain" />
                  <div className="flex items-center justify-between rounded-lg bg-black/20 px-4 py-3 text-xs text-gray-400">
                    <span>{processed.width} × {processed.height}px</span>
                    <span>{(processed.sizeBytes / 1024).toFixed(1)} KB · WebP</span>
                    <button type="button" onClick={clearProcessed} className="text-gray-500 hover:text-red-400 transition-colors">Ganti</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-5 py-3">
          <button
            type="button" onClick={onClose}
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-gray-400 hover:bg-white/5"
          >
            Batal
          </button>
          {activeTab === 'assets' ? (
            <button
              type="button" onClick={handlePick} disabled={!selectedId}
              className="rounded-lg px-6 py-2 text-sm font-medium text-white disabled:opacity-40"
              style={{ backgroundColor: 'var(--yp-pink)' }}
            >
              Pilih
            </button>
          ) : (
            <button
              type="button" onClick={handleUploadAndPick} disabled={!processed || uploading}
              className="rounded-lg px-6 py-2 text-sm font-medium text-white disabled:opacity-40"
              style={{ backgroundColor: 'var(--yp-pink)' }}
            >
              {uploading ? 'Mengupload...' : 'Upload & Pilih'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
